import { useState } from 'react';
import api from '../api/client';

const SOURCES = [
  { value: 'eruvaka', label: 'Eruvaka (PondLogs)', desc: 'Reporte "PM Daily Feed"', icono: '🐟', activo: true },
  { value: 'aq1',     label: 'AQ1',                desc: 'Próximamente',             icono: '🔧', activo: false },
  { value: 'otros',   label: 'Otros',              desc: 'Columnas del proyecto',     icono: '📋', activo: true },
];

/* Columnas esperadas para el origen "Otros" */
const PLANTILLA_COLUMNAS = [
  { col: 'Sector',              ejemplo: 'Sector A.1',                       req: true,  desc: 'Nombre exacto del sector en BD' },
  { col: 'Piscina',             ejemplo: 'Ps 06',                            req: true,  desc: 'Código exacto de la piscina' },
  { col: 'Fecha',               ejemplo: '2026-10-12',                       req: true,  desc: 'Acepta YYYY-MM-DD o DD/MM/YYYY' },
  { col: 'Dosificado Pondlogs', ejemplo: '199.27',                           req: true,  desc: 'kg de balanceado dosificado ese día' },
  { col: 'Corrida',             ejemplo: '5',                                req: false, desc: 'Opcional. Si no viene, se ignora' },
  { col: 'Modo Alimentación',   ejemplo: 'Automático',                       req: false, desc: 'Cronograma / Automático / Híbrido / Sónico' },
  { col: 'Nivel Alimentación',  ejemplo: 'Normal',                           req: false, desc: 'Económico / Normal / Agresivo' },
  { col: 'Tipo Balanceado',     ejemplo: 'BPM 2.0 Nicovita Qualis Forte',    req: false, desc: 'Nombre del tipo de balanceado' },
];

export default function ImportarDosisModal({ onClose, onSuccess }) {
  const [source, setSource] = useState('eruvaka');
  const [file, setFile] = useState(null);
  const [nombreArchivo, setNombreArchivo] = useState('');
  const [preview, setPreview] = useState(null);
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  const [cargandoPreview, setCargandoPreview] = useState(false);

  const sourceActual = SOURCES.find((s) => s.value === source);

  const handleArchivo = async (e) => {
    const archivo = e.target.files?.[0];
    if (!archivo) return;
    setFile(archivo);
    setNombreArchivo(archivo.name);
    setError(null);
    setResultado(null);
    setPreview(null);
    await cargarPreview(archivo);
  };

  const cargarPreview = async (archivo) => {
    setCargandoPreview(true);
    try {
      const fd = new FormData();
      fd.append('archivo', archivo);
      fd.append('source', source);

      const { data } = await api.post('/importar/alimentacion-diaria?preview=true', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al procesar el archivo');
    } finally {
      setCargandoPreview(false);
    }
  };

  const importar = async () => {
    if (!file) return;
    setImportando(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      fd.append('source', source);

      const { data } = await api.post('/importar/alimentacion-diaria', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setResultado(data);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al importar');
    } finally {
      setImportando(false);
    }
  };

  const reset = () => {
    setFile(null);
    setNombreArchivo('');
    setPreview(null);
    setResultado(null);
    setError(null);
  };

  const puedeCerrar = !importando && !cargandoPreview;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      onClick={() => puedeCerrar && onClose?.()}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 bg-slate-700 text-white flex justify-between items-center">
          <div>
            <h2 className="text-base font-bold">📥 Importar desde Excel</h2>
            <p className="text-xs text-slate-300">
              Selecciona el origen, sube el archivo y revisa el preview antes de confirmar
            </p>
          </div>
          <button
            onClick={() => puedeCerrar && onClose?.()}
            className="w-8 h-8 rounded-full hover:bg-white/10 text-sm"
            disabled={!puedeCerrar}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto flex-1">

          {/* ═══ PASO 1: Selector de origen ═══ */}
          {!resultado && (
            <div className="mb-5">
              <div className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wider">
                Origen de los datos
              </div>
              <div className="grid grid-cols-3 gap-3">
                {SOURCES.map((s) => {
                  const seleccionado = source === s.value;
                  const deshabilitado = !s.activo;
                  return (
                    <button
                      key={s.value}
                      type="button"
                      disabled={deshabilitado}
                      onClick={() => !deshabilitado && (setSource(s.value), reset())}
                      className={`p-3 rounded-lg border-2 text-left transition relative ${
                        seleccionado && !deshabilitado
                          ? 'border-primary bg-blue-50 shadow-sm'
                          : deshabilitado
                            ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="text-2xl mb-1">{s.icono}</div>
                      <div className={`font-semibold text-sm ${
                        seleccionado && !deshabilitado ? 'text-primary' : 'text-gray-700'
                      }`}>
                        {s.label}
                      </div>
                      <div className="text-[10px] text-gray-500 mt-0.5 leading-tight">
                        {s.desc}
                      </div>
                      {seleccionado && !deshabilitado && (
                        <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-primary text-white text-[10px] flex items-center justify-center">
                          ✓
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Info contextual por origen */}
          {!resultado && source === 'eruvaka' && (
            <div className="mb-4 p-3 bg-blue-50 text-blue-900 rounded text-xs">
              💡 <strong>Eruvaka / PondLogs:</strong> sube el reporte <em>"PM Daily Feed"</em> (.xlsx).
              Auto-agrupación por <em>Sector + Piscina + Fecha</em>.
              <br />
              <span className="text-[11px] text-blue-700 mt-1 block">
                Columnas usadas: <strong>Location, Pond, Date, Dispensed Feed (Kgs), Mode, Feeding Level</strong>.
              </span>
            </div>
          )}

          {!resultado && source === 'otros' && (
            <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded">
              <div className="text-sm font-bold text-amber-900 mb-2">
                📋 Columnas esperadas por el proyecto
              </div>
              <p className="text-xs text-amber-800 mb-3">
                Sube un .xlsx con al menos estas columnas. Los nombres se comparan sin distinguir
                mayúsculas ni tildes. Las marcadas con <span className="text-red-600 font-bold">*</span> son obligatorias.
              </p>
              <div className="overflow-x-auto bg-white rounded border border-amber-100">
                <table className="w-full text-xs">
                  <thead className="bg-amber-100">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-semibold">Columna</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Ejemplo</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Descripción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {PLANTILLA_COLUMNAS.map((c) => (
                      <tr key={c.col} className="border-t border-amber-100">
                        <td className="px-2 py-1.5 font-mono text-[11px]">
                          {c.req && <span className="text-red-600 font-bold mr-1">*</span>}
                          {c.col}
                        </td>
                        <td className="px-2 py-1.5 text-gray-600 text-[11px]">{c.ejemplo}</td>
                        <td className="px-2 py-1.5 text-gray-500 text-[11px]">{c.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-800 rounded text-sm whitespace-pre-wrap">
              ❌ {error}
            </div>
          )}

          {/* ═══ PASO 2: Upload ═══ */}
          {!resultado && !file && (
            <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition mb-4 ${
              sourceActual?.activo
                ? 'border-gray-300 hover:bg-gray-50'
                : 'border-gray-200 bg-gray-50 cursor-not-allowed'
            }`}>
              <div className="flex flex-col items-center">
                <svg className="w-10 h-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-sm text-gray-600">Haz clic para seleccionar un archivo Excel</p>
                <p className="text-xs text-gray-400 mt-1">.xlsx, .xls</p>
              </div>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleArchivo}
                disabled={!sourceActual?.activo}
                className="hidden"
              />
            </label>
          )}

          {/* Loading preview */}
          {cargandoPreview && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded text-center text-sm text-blue-800">
              ⏳ Analizando archivo... esto puede tardar unos segundos.
            </div>
          )}

          {/* Archivo seleccionado */}
          {file && !resultado && !cargandoPreview && (
            <div className="mb-4 p-2 bg-gray-50 border border-gray-200 rounded text-xs flex items-center justify-between">
              <span className="text-gray-600">
                📎 <strong>{file.name}</strong> · {(file.size / 1024).toFixed(1)} KB
              </span>
              <button type="button" onClick={reset} className="text-red-600 hover:text-red-800 font-medium">
                Quitar
              </button>
            </div>
          )}

          {/* ═══ PASO 3: Preview ═══ */}
          {preview && !resultado && !cargandoPreview && (
            <div className="mb-4">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded mb-3">
                <div className="text-sm font-semibold text-blue-900 mb-1">
                  🔍 Preview — {preview.muestras?.length || 0} de {preview.grupos} filas agrupadas
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs text-blue-800 mt-2">
                  <div><strong>Filas leídas:</strong> {preview.filasLeidas}</div>
                  <div><strong>Grupos únicos:</strong> {preview.grupos}</div>
                  <div><strong>Errores:</strong> {preview.errores?.length || 0}</div>
                </div>
              </div>

              <div className="bg-white border rounded overflow-hidden max-h-[340px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-semibold">Sector</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Piscina</th>
                      <th className="text-center px-2 py-1.5 font-semibold">Corrida</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Fecha</th>
                      <th className="text-right px-2 py-1.5 font-semibold">Dosificado (kg)</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Modo</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Nivel</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Balanceado</th>
                      <th className="text-center px-2 py-1.5 font-semibold">Filas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.muestras?.map((m, i) => (
                      <tr key={i} className="border-t hover:bg-blue-50/40">
                        <td className="px-2 py-1">{m.sector_nombre}</td>
                        <td className="px-2 py-1 font-medium">{m.piscina_codigo}</td>
                        <td className="px-2 py-1 text-center text-gray-600">{m.corrida || '-'}</td>
                        <td className="px-2 py-1">{m.fecha}</td>
                        <td className="px-2 py-1 text-right font-semibold text-green-800">
                          {Number(m.dosificado).toFixed(2)}
                        </td>
                        <td className="px-2 py-1 text-gray-700">{m.modo || '-'}</td>
                        <td className="px-2 py-1 text-gray-700">{m.nivel || '-'}</td>
                        <td className="px-2 py-1 text-gray-600 text-[10px]">{m.tipo_balanceado || '-'}</td>
                        <td className="px-2 py-1 text-center text-gray-500">{m.filas_origen}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {preview.grupos > 20 && (
                <div className="text-xs text-gray-500 text-center mt-1">
                  Mostrando las primeras 20 de {preview.grupos} filas agrupadas.
                </div>
              )}

              {preview.errores?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs font-semibold text-red-700 mb-1">
                    ⚠️ {preview.errores.length} errores detectados:
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded p-2 max-h-40 overflow-y-auto text-xs text-red-700">
                    {preview.errores.slice(0, 30).map((e, i) => (
                      <div key={i}>
                        <strong>Fila {e.fila}:</strong> {e.error}
                      </div>
                    ))}
                    {preview.errores.length > 30 && (
                      <div className="italic text-gray-500 mt-1">
                        ...y {preview.errores.length - 30} más
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ═══ PASO 4: Resultado ═══ */}
          {resultado && (
            <div className="bg-green-50 border border-green-200 rounded p-4 mb-4">
              <h3 className="font-semibold text-green-800 mb-3">✅ Importación completada</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div>
                  <div className="text-xs text-gray-600">Filas leídas</div>
                  <div className="text-xl font-bold text-gray-700">{resultado.filasLeidas ?? 0}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-600">Grupos únicos</div>
                  <div className="text-xl font-bold text-blue-700">{resultado.grupos ?? 0}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-600">Creados</div>
                  <div className="text-xl font-bold text-green-700">{resultado.creados ?? 0}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-600">Actualizados</div>
                  <div className="text-xl font-bold text-orange-700">{resultado.actualizados ?? 0}</div>
                </div>
              </div>

              {resultado.errores?.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs font-semibold text-red-700 mb-1">
                    Errores ({resultado.errores.length})
                  </div>
                  <div className="bg-white border border-red-200 rounded p-2 max-h-40 overflow-y-auto text-xs text-red-700 whitespace-pre-wrap">
                    {resultado.errores.slice(0, 30).map((e, i) => (
                      <div key={i}>
                        <strong>{e.fila ? `Fila ${e.fila}:` : 'Error:'}</strong> {e.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-gray-50 border-t flex justify-end gap-2">
          {resultado ? (
            <>
              <button onClick={reset} className="px-4 py-1.5 border rounded text-sm hover:bg-gray-100">
                Importar otro
              </button>
              <button
                onClick={() => onClose?.()}
                className="px-4 py-1.5 bg-slate-700 text-white rounded text-sm font-medium hover:bg-slate-800"
              >
                Cerrar
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => puedeCerrar && onClose?.()}
                disabled={!puedeCerrar}
                className="px-4 py-1.5 border rounded text-sm hover:bg-gray-100 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={importar}
                disabled={!preview || !file || importando || cargandoPreview || !sourceActual?.activo}
                className="px-5 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
                title={
                  !sourceActual?.activo
                    ? 'Próximamente'
                    : !preview
                      ? 'Sube un archivo primero'
                      : `Confirmar importación de ${preview.grupos} filas`
                }
              >
                {importando
                  ? '⏳ Guardando...'
                  : preview
                    ? `✅ Confirmar importación (${preview.grupos} filas)`
                    : '✅ Importar'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}